import React, {useState} from 'react';
import {useMutation, gql} from '@apollo/client';
import FCWithQuery from '@src/util/FCWithQuery';
import {
  SubmitContactUsFormMutation,
  SubmitContactUsFormMutationVariables,
} from '@src/__generated__/SubmitContactUsFormMutation';
import {EzManageNavEmailModalCatererAccount} from '@src/__generated__/EzManageNavEmailModalCatererAccount';
import isEmail from '@src/util/isEmail';
import {useTranslation} from 'react-i18next';
import EmailModalLayout from './EmailModalLayout';

type EmailModalProps = {
  catererAccount: EzManageNavEmailModalCatererAccount | null;
  onDismiss: () => void;
  switchToSupportModal: () => void;
};

const EmailModal: FCWithQuery<EmailModalProps> = {
  Component: ({catererAccount, onDismiss, switchToSupportModal}) => {
    const {t: useMessage} = useTranslation();
    const [values, setValues] = useState<SubmitContactUsFormMutationVariables>({
      email: (catererAccount && catererAccount.email) || '',
      name: (catererAccount && catererAccount.name) || '',
      message: '',
    });

    const noErrorState = {email: false, message: false, name: false};

    const [errors, setErrors] = useState(noErrorState);
    /* eslint-disable @typescript-eslint/indent */
    const [submitContactUsForm] = useMutation<
      SubmitContactUsFormMutation,
      SubmitContactUsFormMutationVariables
    >(submitContactUsFormMutation);
    /* eslint-enable @typescript-eslint/indent */

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const {name, value} = e.currentTarget;
      setValues({...values, [name.toLowerCase()]: value});
    };

    const validate = () => {
      let allFieldsValid = true;
      const invalidFields = {...noErrorState};

      if (!values.email || !isEmail(values.email)) {
        allFieldsValid = false;
        invalidFields.email = true;
      }

      if (!values.message || values.message.length < 1) {
        allFieldsValid = false;
        invalidFields.message = true;
      }

      if (!values.name || values.name.length < 1) {
        allFieldsValid = false;
        invalidFields.name = true;
      }

      setErrors({...errors, ...invalidFields});

      return allFieldsValid;
    };

    const handleSubmit = async () => {
      if (!validate()) return;

      const submitResult = await submitContactUsForm({
        variables: values,
      });

      const success =
        submitResult.data &&
        submitResult.data.submitContactUsForm &&
        submitResult.data.submitContactUsForm.success;

      if (success) onDismiss();
    };

    return (
      <EmailModalLayout
        errors={errors}
        onChange={handleChange}
        onDismiss={onDismiss}
        onSubmit={handleSubmit}
        switchToSupportModal={switchToSupportModal}
        useMessage={useMessage}
        values={values}
      />
    );
  },
  fragment: gql`
    fragment EzManageNavEmailModalCatererAccount on CatererAccount {
      id
      name
      email
    }
  `,
};

export const submitContactUsFormMutation = gql`
  mutation SubmitContactUsFormMutation($name: String, $email: String, $message: String) {
    submitContactUsForm(fullName: $name, email: $email, message: $message) {
      success
    }
  }
`;

export default EmailModal;
